import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { MessageCircle, Users, Building2, GraduationCap, Heart, Briefcase, CheckCircle, ArrowDown } from 'lucide-react';
import { Header, Footer } from '../components/Layout';
import { API } from '../utils/api';


function CTAButton() {
  const scrollToForm = () => {
    document.getElementById('contacto-form').scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <Button 
      onClick={scrollToForm}
      className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-8 py-3 text-lg"
    >
      <MessageCircle className="w-5 h-5 mr-2" />
      Contactar
    </Button>
  );
}

function SectionDivider() {
  return (
    <div className="flex justify-center my-8">
      <ArrowDown className="w-6 h-6 text-amber-500/50 animate-bounce" />
    </div>
  );
}

export default function ZonaCeroPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    whatsapp: '',
    region: '',
    tipo_taller: '',
    mensaje: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre || !formData.email || !formData.whatsapp || !formData.region || !formData.tipo_taller) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(API + '/zona-cero/contacto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setSent(true);
        toast.success('¡Mensaje enviado! Te contactaremos pronto.');
        setFormData({ nombre: '', email: '', whatsapp: '', region: '', tipo_taller: '', mensaje: '' });
      } else {
        throw new Error('Error al enviar');
      }
    } catch (e) {
      toast.error('Error al enviar el mensaje. Intenta nuevamente.');
    }
    setSending(false);
  };

  return (
    <>
      
        <title>Charlas Prevención Drogas y Adicciones para Empresas y Colegios | Zona Cero Chile</title>
        <meta name="description" content="Charlas y talleres de prevención de drogas, alcohol y adicciones para empresas, colegios y organizaciones en Chile. Testimonios reales de recuperación, evidencia clínica y herramientas prácticas. Capacitación laboral y escolar." />
        <meta name="keywords" content="charlas prevención drogas Chile, talleres adicciones empresas, prevención consumo alcohol trabajo, capacitación drogas colegios, charlas SENDA, prevención adicciones laborales, talleres salud mental organizaciones, charlas consumo problemático, prevención drogas adolescentes, programa prevención alcohol empresas" />
        <link rel="canonical" href="https://sinadicciones.org/zona-cero" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Zona Cero - Charlas Prevención Drogas y Adicciones | Chile" />
        <meta property="og:description" content="Charlas sobre consumo, adicciones y salud mental para empresas, colegios y organizaciones. Testimonios reales de recuperación y evidencia clínica." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/zona-cero" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="800" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Zona Cero - Charlas Prevención Drogas Chile" />
        <meta name="twitter:description" content="Charlas y talleres de prevención de adicciones para empresas y colegios. Testimonios reales y herramientas prácticas." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Zona Cero - Charlas de Prevención de Adicciones",
            "description": "Charlas y talleres sobre prevención de drogas, alcohol y adicciones para empresas, colegios, municipalidades y organizaciones en Chile",
            "provider": {
              "@type": "Organization",
              "name": "Sin Adicciones Chile",
              "url": "https://sinadicciones.org"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Chile"
            },
            "serviceType": ["Charlas de prevención", "Capacitación laboral", "Talleres educativos", "Prevención de adicciones"],
            "offers": [
              {
                "@type": "Offer",
                "name": "Charla para Colegios",
                "price": "180000",
                "priceCurrency": "CLP",
                "description": "Charla de prevención de drogas para estudiantes, apoderados o equipos docentes"
              },
              {
                "@type": "Offer",
                "name": "Charla para Municipalidades",
                "price": "150000",
                "priceCurrency": "CLP",
                "description": "Charla para equipos de atención primaria y funcionarios municipales"
              },
              {
                "@type": "Offer",
                "name": "Charla para Empresas",
                "price": "450000",
                "priceCurrency": "CLP",
                "description": "Charla de prevención de consumo problemático en el ámbito laboral"
              }
            ]
          }
        `}</script>
      
      
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 overflow-hidden" aria-labelledby="hero-heading">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-40"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-6 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
                <span className="text-amber-400 font-medium">Charlas de Prevención de Adicciones en Chile</span>
              </div>
              
              <h1 id="hero-heading" className="text-5xl md:text-7xl font-black text-white mb-6 tracking-tight">
                ZONA <span className="text-amber-500">CERO</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-slate-300 mb-4 leading-relaxed max-w-3xl mx-auto">
                <strong>Charlas y talleres sobre prevención de drogas, alcohol y adicciones</strong> para empresas, colegios y organizaciones que quieren tener <span className="text-amber-400">la conversación que nadie está teniendo</span>.
              </p>
              
              <p className="text-base text-slate-400 mb-8 max-w-2xl mx-auto">
                Capacitación laboral y escolar con testimonios reales de recuperación, evidencia clínica actualizada y herramientas prácticas de detección y derivación.
              </p>
              
              <CTAButton />
            </div>
          </div>
        </section>

        {/* Image Section */}
        <section className="py-8" aria-label="Imagen de charla preventiva">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <figure>
                <img 
                  src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80" 
                  alt="Charla de prevención de drogas y adicciones en empresa - Capacitación laboral sobre consumo problemático de alcohol y sustancias para trabajadores en Chile"
                  title="Charla preventiva Zona Cero - Prevención de adicciones en el trabajo"
                  className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl shadow-black/50"
                  loading="eager"
                  width="1200"
                  height="400"
                />
                <figcaption className="sr-only">
                  Charla de prevención de adicciones en ambiente laboral - Zona Cero ofrece talleres y capacitaciones sobre consumo de drogas y alcohol para empresas en Chile
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Quiénes Somos */}
        <section className="py-16" aria-labelledby="quienes-somos-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 id="quienes-somos-heading" className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Quiénes <span className="text-amber-500">somos</span>: Expertos en Prevención de Adicciones
              </h2>
              
              <div className="prose prose-lg prose-invert max-w-none">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Zona Cero nace de una experiencia que no se puede fabricar: <strong className="text-white">la de haber estado adentro</strong>.
                </p>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  <strong className="text-amber-400">Nelson González</strong> fundó SinAdicciones.org después de años de dependencia al alcohol y la cocaína, pérdidas reales y un proceso de recuperación que lo llevó a construir el directorio más completo de centros de rehabilitación de Chile.
                </p>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  <strong className="text-amber-400">Ricardo Manzur Carrasco</strong> es periodista con más de 20 años de experiencia, ex editor nacional de La Cuarta, certificado por OPS/OMS y SENDA en neurobiología del consumo y política de drogas. También completó un proceso formal de recuperación de dependencia severa al alcohol.
                </p>
                
                <p className="text-slate-400 text-lg italic border-l-4 border-amber-500 pl-6 my-8">
                  No somos coaches. No somos terapeutas. Somos dos personas que vivieron esto desde adentro y que decidieron que esa experiencia tenía que servir para algo.
                </p>
              </div>
              
              <div className="flex justify-center mt-10">
                <CTAButton />
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Por qué elegirnos */}
        <section className="py-16 bg-slate-900/50" aria-labelledby="por-que-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 id="por-que-heading" className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Por qué elegir nuestras <span className="text-amber-500">charlas de prevención</span>
              </h2>
              
              <div className="prose prose-lg prose-invert max-w-none">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Hay muchas personas que hablan de adicciones en Chile. Psicólogos, psiquiatras, trabajadores sociales, académicos. Todos tienen algo valioso que aportar. <strong className="text-white">Nosotros tenemos algo distinto.</strong>
                </p>
                
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 my-8">
                  <p className="text-white text-lg leading-relaxed mb-0">
                    Ningún profesional de la salud, por muy bien formado que esté, puede pararse frente a un grupo y decir: <em className="text-amber-400">"Yo estuve ahí. Yo perdí el control. Yo le mentí a mi familia. Yo llegué a un centro de rehabilitación sin saber si iba a poder salir. Y hoy estoy aquí, construyendo algo con esa experiencia."</em>
                  </p>
                </div>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Eso no se estudia. No se certifica. No se contrata en una agencia.
                </p>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  La evidencia clínica es clara: <strong className="text-white">el estigma es la principal barrera</strong> que impide que las personas busquen ayuda a tiempo. Y el estigma se reduce cuando quien habla del tema lo hace sin vergüenza, con nombre y apellido, desde adentro. No desde un manual.
                </p>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Lo que ofrecemos no es una charla motivacional ni una clase magistral sobre neurociencia. Es una conversación honesta sobre algo que le pasa al <strong className="text-amber-400">28,4% de los trabajadores en Chile</strong>, a estudiantes de todos los colegios del país, a familias que no saben cómo nombrar lo que están viviendo.
                </p>
                
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Esa conversación, cuando la da alguien que realmente la vivió y que además sabe cómo traducirla en evidencia clínica y recursos concretos, no se olvida. No se procesa como información. <strong className="text-white">Se procesa como permiso</strong>: el permiso para hablar, para pedir ayuda, para reconocer que el problema existe sin sentir que eso te define para siempre.
                </p>
                
                <p className="text-amber-400 text-xl font-semibold text-center my-8">
                  Eso es lo que hacemos. Y no hay mucha gente que pueda hacerlo.
                </p>
              </div>
              
              <div className="flex justify-center mt-10">
                <CTAButton />
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Qué ofrecemos */}
        <section className="py-16" aria-labelledby="que-ofrecemos-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 id="que-ofrecemos-heading" className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Talleres y <span className="text-amber-500">capacitaciones</span> sobre adicciones
              </h2>
              
              <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-2xl p-8 mb-8">
                <p className="text-white text-xl leading-relaxed mb-6">
                  Una charla de entre <strong>60 y 90 minutos</strong> que combina testimonio real, evidencia clínica actualizada y herramientas prácticas para quien escucha. <span className="text-amber-400">Sin moralismos. Sin eufemismos. Sin el discurso de siempre.</span>
                </p>
              </div>
              
              <p className="text-slate-300 text-lg mb-6">
                El contenido se adapta al perfil del público, pero los ejes son siempre los mismos:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Cómo funciona el cerebro en la adicción y por qué la voluntad sola no alcanza",
                  "La diferencia entre consumo de riesgo, consumo problemático y dependencia, y cómo detectarlos a tiempo",
                  "Por qué el 28,4% de los trabajadores en Chile muestra indicadores de consumo problemático",
                  "Cómo hablar del tema sin estigmatizar",
                  "Qué hacer cuando alguien cercano necesita ayuda y no sabe cómo pedirla",
                  "Dónde buscar ayuda en Chile y cómo elegir bien"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-center mt-10">
                <CTAButton />
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Para quién es cada charla */}
        <section className="py-16 bg-slate-900/50" aria-labelledby="precios-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 id="precios-heading" className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
                Charlas de prevención <span className="text-amber-500">para cada organización</span>
              </h2>
              <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
                Talleres de prevención de drogas y alcohol adaptados para empresas, colegios, municipalidades y organizaciones. Precios y duraciones según el tipo de audiencia.
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Colegios */}
                <Card className="bg-slate-800/80 border-slate-700 hover:border-amber-500/50 transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mb-4">
                      <GraduationCap className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">Colegios</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Dirigida a estudiantes de enseñanza media, apoderados o equipos docentes. El foco está en consumo adolescente, presión de grupo, detección temprana y cómo tener la conversación difícil en casa.
                    </p>
                    <p className="text-amber-400 text-sm font-medium mb-2">
                      Después de Calama, esta charla no es opcional. Es urgente.
                    </p>
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <p className="text-slate-500 text-sm">60-75 min + preguntas</p>
                      <p className="text-2xl font-bold text-white">$180.000</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Municipalidades */}
                <Card className="bg-slate-800/80 border-slate-700 hover:border-amber-500/50 transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Building2 className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">Municipalidades</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Dirigida a equipos de atención primaria, funcionarios municipales o comunidades en riesgo. El foco está en la red de apoyo disponible en Chile, cómo activarla y cómo acompañar a una familia en crisis.
                    </p>
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <p className="text-slate-500 text-sm">60 min + preguntas</p>
                      <p className="text-2xl font-bold text-white">$150.000</p>
                    </div>
                  </CardContent>
                </Card>

                {/* ONGs */}
                <Card className="bg-slate-800/80 border-slate-700 hover:border-amber-500/50 transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Heart className="w-6 h-6 text-pink-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">ONGs y Fundaciones</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Dirigida a equipos que trabajan con poblaciones vulnerables. El foco está en reducción de daño, derivación efectiva y autocuidado del equipo.
                    </p>
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <p className="text-slate-500 text-sm">60 min + preguntas</p>
                      <p className="text-2xl font-bold text-white">$120.000</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Empresas pequeñas */}
                <Card className="bg-slate-800/80 border-slate-700 hover:border-amber-500/50 transition-all">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">Empresas hasta 50 personas</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Dirigida a equipos de trabajo o mandos medios. El foco está en el paciente funcional: el trabajador que rinde, cumple y se destruye en silencio. Incluye protocolo básico de detección y derivación.
                    </p>
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <p className="text-slate-500 text-sm">75-90 min + preguntas</p>
                      <p className="text-2xl font-bold text-white">$450.000</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Empresas grandes */}
                <Card className="bg-slate-800/80 border-slate-700 hover:border-amber-500/50 transition-all md:col-span-2 lg:col-span-1">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                      <Briefcase className="w-6 h-6 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">Empresas +50 personas</h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Mismo contenido con capacidad ampliada y posibilidad de sesión de preguntas anónimas por escrito para quienes no quieran intervenir en público.
                    </p>
                    <div className="pt-4 border-t border-slate-700 mt-4">
                      <p className="text-slate-500 text-sm">90 min + preguntas</p>
                      <p className="text-2xl font-bold text-white">$650.000</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex justify-center mt-12">
                <CTAButton />
              </div>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Qué no somos */}
        <section className="py-16" aria-labelledby="alcance-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 id="alcance-heading" className="text-3xl md:text-4xl font-bold text-white mb-8">
                Alcance de nuestras <span className="text-amber-500">charlas preventivas</span>
              </h2>
              
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <p className="text-slate-300 text-lg leading-relaxed">
                  No reemplazamos a un psicólogo, un psiquiatra ni un programa de tratamiento. No hacemos diagnósticos. No damos consejería individual.
                </p>
                <p className="text-white text-xl font-medium mt-6">
                  Somos <span className="text-amber-400">el primer paso en la prevención</span>: abrimos la conversación sobre drogas y alcohol, reducimos el estigma y conectamos a quien escucha con los centros de rehabilitación y recursos de tratamiento que existen en Chile.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Formulario de Contacto */}
        <section id="contacto-form" className="py-20 bg-gradient-to-b from-slate-900 to-slate-950" aria-labelledby="contacto-heading">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto">
              <h2 id="contacto-heading" className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
                Solicita una <span className="text-amber-500">charla de prevención</span>
              </h2>
              <p className="text-slate-400 text-center mb-10">
                Cuéntanos sobre tu empresa, colegio u organización y te enviaremos una propuesta personalizada de capacitación en prevención de adicciones.
              </p>
              
              {sent ? (
                <div className="text-center py-12 bg-slate-800/50 rounded-2xl border border-emerald-500/30">
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">¡Mensaje enviado!</h3>
                  <p className="text-slate-400">Te contactaremos pronto para coordinar los detalles.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
                  <div>
                    <Label className="text-slate-300 mb-2 block">Nombre completo *</Label>
                    <Input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      placeholder="Tu nombre"
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="tu@email.com"
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-slate-300 mb-2 block">WhatsApp *</Label>
                    <Input
                      type="tel"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      placeholder="+56 9 1234 5678"
                      className="bg-slate-900 border-slate-700 text-white"
                      required
                      data-testid="zonacero-whatsapp-input"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-300 mb-2 block">Región *</Label>
                    <select
                      value={formData.region}
                      onChange={(e) => setFormData({...formData, region: e.target.value})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                      required
                      data-testid="zonacero-region-select"
                    >
                      <option value="">Selecciona tu región</option>
                      <option value="Arica y Parinacota">Arica y Parinacota</option>
                      <option value="Tarapacá">Tarapacá</option>
                      <option value="Antofagasta">Antofagasta</option>
                      <option value="Atacama">Atacama</option>
                      <option value="Coquimbo">Coquimbo</option>
                      <option value="Valparaíso">Valparaíso</option>
                      <option value="Metropolitana">Metropolitana</option>
                      <option value="O'Higgins">O'Higgins</option>
                      <option value="Maule">Maule</option>
                      <option value="Ñuble">Ñuble</option>
                      <option value="Biobío">Biobío</option>
                      <option value="La Araucanía">La Araucanía</option>
                      <option value="Los Ríos">Los Ríos</option>
                      <option value="Los Lagos">Los Lagos</option>
                      <option value="Aysén">Aysén</option>
                      <option value="Magallanes">Magallanes</option>
                      <option value="Otro país / Latinoamérica">Otro país / Latinoamérica</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Tipo de taller *</Label>
                    <Select 
                      value={formData.tipo_taller} 
                      onValueChange={(v) => setFormData({...formData, tipo_taller: v})}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        <SelectItem value="colegios" className="text-white">Colegios</SelectItem>
                        <SelectItem value="municipalidades" className="text-white">Municipalidades</SelectItem>
                        <SelectItem value="organizaciones" className="text-white">ONGs / Fundaciones</SelectItem>
                        <SelectItem value="empresas" className="text-white">Empresas</SelectItem>
                        <SelectItem value="otros" className="text-white">Otros</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300 mb-2 block">Mensaje (opcional)</Label>
                    <Textarea
                      value={formData.mensaje}
                      onChange={(e) => setFormData({...formData, mensaje: e.target.value})}
                      placeholder="Cuéntanos más sobre tu organización y lo que necesitas..."
                      className="bg-slate-900 border-slate-700 text-white min-h-[120px]"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={sending}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold py-6 text-lg"
                  >
                    {sending ? 'Enviando...' : 'Enviar mensaje'}
                  </Button>
                  
                  <p className="text-slate-500 text-sm text-center">
                    Te responderemos en menos de 24 horas.
                  </p>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
}
